import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerClustererF,
} from "@react-google-maps/api";
import {
  Hash,
  Battery,
  Wifi,
  Clock,
  MapPin,
  Gauge,
  Calendar,
  Activity,
  AlertTriangle,
  TrendingUp,
  X,
} from "lucide-react";
import {
  Modal,
  Spin,
  Input,
  ConfigProvider,
  DatePicker,
  theme as antTheme,
  Tooltip as AntTooltip,
} from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Dot,
} from "recharts";
import { getMeterLocationList } from "../services/index";
import { useDistrict } from "../utils/DistrictContext";
import { useTheme } from "../context/ThemeContext";
import { get } from "../Libraries/Ajax/HttpService";
import dayjs from "dayjs";
import clusterIcon from "../assets/images/m1.png";

const { Search } = Input;
const { RangePicker } = DatePicker;

const containerStyle = {
  width: "100%",
  height: "86vh",
  borderRadius: "12px",
  position: "relative",
};

const MapTab = () => {
  const { theme, colors } = useTheme();
  const { selectedDistrict } = useDistrict();

  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [map, setMap] = useState(null);
  const [filterType, setFilterType] = useState("active");
  const [colorFilter, setColorFilter] = useState("all");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMeterData, setSelectedMeterData] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [anomalyData, setAnomalyData] = useState([]);
  const [hardwareDetails, setHardwareDetails] = useState(null);
  const [startDate, setStartDate] = useState(
    dayjs().subtract(1, "month").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));

  const markersRef = useRef([]);
  const clustererRef = useRef(null);
  const lastDistrictRef = useRef(null);
  const hasInitializedDistrict = useRef(false);
  const mapStateRef = useRef({
    center: null,
    zoom: 11,
  });
  // Near your other refs
  const isReturningRef = useRef(false);
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  });
  // 1. Manage the center and zoom in state to prevent the "White Screen"
  const [mapPosition, setMapPosition] = useState({
    center: { lat: 29.92, lng: -95.2 },
    zoom: 11,
  });

  // 2. Capture movement ONLY when user is browsing (Modal closed)
  const syncMapPosition = useCallback(() => {
    if (map && !isModalOpen && !isReturningRef.current) {
      const newCenter = map.getCenter();
      setMapPosition({
        center: { lat: newCenter.lat(), lng: newCenter.lng() },
        zoom: map.getZoom(),
      });
    }
  }, [map, isModalOpen]);
  // --- 1. DATA FETCHING ---
  useEffect(() => {
    const fetchMeters = async () => {
      setLoading(true);
      try {
        const response = await getMeterLocationList(filterType);
        const apiData = response?.data?.meters || [];
        const seen = new Set();
        const uniqueData = apiData.filter((m) => {
          const key = `${m.location_id}-${m.meter_id}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setMeters(uniqueData);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    if (isLoaded) fetchMeters();
  }, [selectedDistrict, filterType, isLoaded]);
  // Add this near your other useCallback/useEffect hooks
  const handleMapIdle = useCallback(() => {
    // ONLY record position if we aren't viewing a meter AND aren't currently "returning" home
    if (map && !isModalOpen && !isReturningRef.current) {
      const center = map.getCenter();
      mapStateRef.current = {
        center: { lat: center.lat(), lng: center.lng() },
        zoom: map.getZoom(),
      };
    }
  }, [map, isModalOpen]);

  // --- 2. PERFORMANCE FILTERING ---
  const filteredMeters = useMemo(() => {
    return meters.filter((m) => {
      const query = searchQuery.toLowerCase();
      const statusMatch =
        colorFilter === "all" || m.operational?.toLowerCase() === colorFilter;
      const searchMatch =
        !query ||
        m.meter_id?.toLowerCase().includes(query) ||
        m.location_address?.toLowerCase().includes(query);
      return statusMatch && searchMatch;
    });
  }, [meters, searchQuery, colorFilter]);

  const operationalCounts = useMemo(() => {
    const counts = { all: meters.length, green: 0, orange: 0, red: 0 };
    meters.forEach((m) => {
      const status = m.operational?.toLowerCase();
      if (counts[status] !== undefined) counts[status]++;
    });
    return counts;
  }, [meters]);

  useEffect(() => {
    if (!map || !isLoaded || !clustererRef.current || !window.google) return;

    // --- MARKER MANAGEMENT ---
    markersRef.current.forEach((marker) => marker && marker.setMap(null));
    clustererRef.current.clearMarkers(true);
    markersRef.current = [];

    const validMarkers = [];

    const occupiedPositions = new Map();
    filteredMeters.forEach((meter) => {
      let lat = Number(meter?.location_latitude);
      let lng = Number(meter?.location_longitude);

      if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0) return;

      // 1. Create a key for this exact location
      const posKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;

      // 2. Check if someone is already there
      const countAtPosition = occupiedPositions.get(posKey) || 0;
      occupiedPositions.set(posKey, countAtPosition + 1);

      // 3. If it's the 2nd, 3rd, etc. meter, nudge it slightly
      if (countAtPosition > 0) {
        // We use a tiny offset (approx 1-2 meters)
        // This is invisible at low zoom but separates them at zoom 20/21
        const offset = countAtPosition * 0.00002;
        lat += offset;
        lng += offset;
      }

      const color =
        meter.operational?.toLowerCase() === "red"
          ? "#ff4d4f"
          : meter.operational?.toLowerCase() === "orange"
            ? "#ffa940"
            : "#00e5a8";

      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        icon: {
          url:
            "data:image/svg+xml;base64," +
            btoa(
              `<svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg"><circle cx="32" cy="32" r="31" fill="white" /><circle cx="32" cy="32" r="27" fill="#0a1628" /><path d="M18 40 A 18 18 0 1 1 46 40" fill="none" stroke="${color}" stroke-width="5" stroke-linecap="round" /><line x1="32" y1="32" x2="44" y2="20" stroke="${color}" stroke-width="7" stroke-linecap="round" /><circle cx="32" cy="32" r="5" fill="white" /></svg>`,
            ),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20),
        },
      });

      marker.addListener("click", () => {
        // Save pre-zoom position into our state
        const c = map.getCenter();
        setMapPosition({
          center: { lat: c.lat(), lng: c.lng() },
          zoom: map.getZoom(),
        });

        setSelectedMeterData(meter);
        setIsModalOpen(true);
        map.panTo({ lat, lng });
        map.setZoom(20);
      });
      validMarkers.push(marker);
    });
    markersRef.current = validMarkers;
    clustererRef.current.addMarkers(validMarkers);

    // --- DISTRICT CENTERING ---
    const districtCenters = {
      FB151: { lat: 29.753417, lng: -95.861396 },
      GPMD: { lat: 29.92312, lng: -95.20392 },
    };

    if (
      selectedDistrict !== lastDistrictRef.current ||
      !hasInitializedDistrict.current
    ) {
      const center = districtCenters[selectedDistrict];
      if (center) {
        setMapPosition({ center, zoom: 15 });
      }
      lastDistrictRef.current = selectedDistrict;
      hasInitializedDistrict.current = true;
    }
  }, [filteredMeters, map, isLoaded, selectedDistrict]);
  const onMapLoad = useCallback((mapInstance) => setMap(mapInstance), []);

  // --- 4. POPUP DATA FETCHING ---
  useEffect(() => {
    if (!isModalOpen || !selectedMeterData) return;
    const fetchDetailedData = async () => {
      setIsModalLoading(true);
      setAnomalyData([]);
      try {
        const res = await get("apis/anoamaly_detection", {
          params: {
            meter_id: selectedMeterData.meter_id,
            start_date: startDate,
            end_date: endDate,
            time_range: "Custom",
          },
        });
        const root = Array.isArray(res.data) ? res.data[0] : res.data || {};
        if (root?.data && root.data.length > 0) {
          setAnomalyData(
            root.data.map((item) => ({
              name: item.day,
              gallons: parseFloat(item.total_daily_flow) || 0,
              threshold: parseFloat(item.threshold_value),
              rolling: parseFloat(item.upper_rolling_threshold),
              historical: parseFloat(item.historical_guardrail_upper),
              isAnomaly: item.anomaly === 1,
              anomalyReason: item.anomaly_reason,
              suppressedReason: item.anomaly_suppressed_reason,
            })),
          );
        } else {
          setAnomalyData([]); // Ensure it's explicitly empty
        }

        setHardwareDetails(root.meter_details?.[0] || null);
      } catch (e) {
        console.error(e);
      } finally {
        setIsModalLoading(false);
      }
    };
    fetchDetailedData();
  }, [isModalOpen, selectedMeterData, startDate, endDate]);

  // --- 5. UI COMPONENTS (TOOLTIP & LEGEND RESTORED) ---
  const RenderCustomLegend = () => {
    const legendItems = [
      { label: "Daily Flow", dash: "none", width: 4.5 },
      { label: "Threshold", dash: "12 6", width: 2.5 },
      { label: "Rolling", dash: "1 10", width: 4 },
      { label: "Historical", dash: "15 4 3 4", width: 1.5 },
    ];
    return (
      <div className="flex flex-wrap justify-center gap-3 mb-6">
        {legendItems.map((item, index) => (
          <div key={index} className="flex items-center gap-3">
            <svg width="45" height="12">
              <line
                x1="0"
                y1="6"
                x2="45"
                y2="6"
                stroke={colors.text}
                strokeWidth={item.width}
                strokeDasharray={item.dash}
                strokeLinecap="round"
              />
            </svg>
            <span
              className="text-[12px] font-bold tracking-wide"
              style={{ color: colors.text }}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const AnomalyTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const formatVal = (val) =>
        val !== null && val !== undefined
          ? `${val.toLocaleString()} Gal`
          : "N/A";
      return (
        <div
          className="p-4 border rounded-lg shadow-lg min-w-[240px]"
          style={{
            backgroundColor: colors.background,
            borderColor: `${colors.primary}30`,
            backdropFilter: "blur(6px)",
          }}
        >
          <p
            className="text-xs mb-3 font-bold border-b pb-2"
            style={{ color: colors.text }}
          >
            {dayjs(label).format("MMM DD, YYYY")}
          </p>
          <div className="space-y-1.5">
            <div className="flex justify-between gap-4">
              <span
                className="text-[11px]"
                style={{ color: colors.textSecondary }}
              >
                Daily Flow:
              </span>
              <span
                className="text-[11px] font-bold"
                style={{ color: colors.primary }}
              >
                {Math.round(data.gallons).toLocaleString()} Gal
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span
                className="text-[11px]"
                style={{ color: colors.textSecondary }}
              >
                Threshold:
              </span>
              <span
                className="text-[11px] font-semibold"
                style={{ color: colors.text }}
              >
                {formatVal(data.threshold)}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span
                className="text-[11px]"
                style={{ color: colors.textSecondary }}
              >
                Rolling:
              </span>
              <span
                className="text-[11px] font-semibold"
                style={{ color: colors.text }}
              >
                {formatVal(data.rolling)}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span
                className="text-[11px]"
                style={{ color: colors.textSecondary }}
              >
                Historical:
              </span>
              <span
                className="text-[11px] font-semibold"
                style={{ color: colors.text }}
              >
                {formatVal(data.historical)}
              </span>
            </div>
          </div>
          {data.suppressedReason && (
            <div
              className="mt-3 pt-2 border-t text-[11px]"
              style={{ color: "#94a3b8" }}
            >
              <strong>Suppressed:</strong> {data.suppressedReason}
            </div>
          )}
          {data.isAnomaly && !data.suppressedReason && (
            <div
              className="mt-3 pt-2 border-t text-[11px]"
              style={{ color: "#f87171" }}
            >
              <div className="font-bold uppercase tracking-wider text-[10px] mb-1">
                ⚠️ Anomaly Detected
              </div>
              <strong>Reason:</strong>{" "}
              {data.anomalyReason || "Threshold exceeded"}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  function SectionHeader({ title, icon }) {
    return (
      <div className="flex items-center gap-2 mt-3">
        <div className="text-cyan-400">{icon}</div>
        <h3 className="text-sm text-slate-300">{title}</h3>
      </div>
    );
  }

  function InfoTile({ label, value, icon }) {
    return (
      <div
        className="rounded-xl border p-4 flex items-start gap-3"
        style={{
          borderColor: colors.border,
          backgroundColor: `${colors.cardBg}0D`,
        }}
      >
        <div className="mt-1" style={{ color: colors.primary }}>
          {icon}
        </div>
        <div>
          <span
            className="text-xs font-semibold block mb-1"
            style={{ color: colors.textSecondary }}
          >
            {label}
          </span>
          <p className="text-sm font-bold" style={{ color: colors.text }}>
            {value || "N/A"}
          </p>
        </div>
      </div>
    );
  }

  if (!isLoaded || loading)
    return (
      <div className="h-[600px] flex items-center justify-center">
        <Spin size="large" tip="Syncing Data..." />
      </div>
    );

  const statusColor =
    hardwareDetails?.m_endpoint_status === "Active" ? "#22c55e" : "#ef4444";

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 w-full max-w-md px-4">
        <Search
          placeholder="Search Meter..."
          allowClear
          enterButton
          size="large"
          onSearch={(v) => setSearchQuery(v)}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* <div
        className="absolute bottom-24 right-4 z-10 p-4 rounded-xl border backdrop-blur-md w-[160px]"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
        }}
      >
        <div
          className="text-[13px] font-bold mb-2"
          style={{ color: colors.text }}
        >
          Filters
        </div>
        <div className="flex gap-1 mb-4">
          <button
            onClick={() => setFilterType("active")}
            className="flex-1 text-[11px] p-1 rounded"
            style={{
              background:
                filterType === "active" ? colors.gradient : colors.cardBg,
              color: colors.text,
            }}
          >
            Active
          </button>
          <button
            onClick={() => setFilterType("all")}
            className="flex-1 text-[11px] p-1 rounded"
            style={{
              background:
                filterType === "all" ? colors.gradient : colors.cardBg,
              color: colors.text,
            }}
          >
            All
          </button>
        </div>
        {["all", "green", "orange", "red"].map((f) => (
          <div
            key={f}
            onClick={() => setColorFilter(f)}
            className="flex items-center justify-between mb-2 cursor-pointer"
            style={{
              // Apply background if selected
              backgroundColor:
                colorFilter === f ? `${colors.primary}20` : "transparent",
              // Optional: add a subtle border or different text color for the active item
              border:
                colorFilter === f
                  ? `1px solid ${colors.primary}40`
                  : "1px solid transparent",
            }}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  background:
                    f === "all"
                      ? "#ccc"
                      : f === "green"
                        ? "#00e5a8"
                        : f === "orange"
                          ? "#ffa940"
                          : "#ff4d4f",
                }}
              />
              <span
                className="text-xs capitalize"
                style={{ color: colors.text }}
              >
                {f}
              </span>
            </div>
            <span
              className="text-[10px]"
              style={{ color: colors.textSecondary }}
            >
              ({operationalCounts[f]})
            </span>
          </div>
        ))}
      </div> */}
      {/* Replace the absolute filters div element container with this section */}
      <div
        className="absolute top-4 right-4 z-10 p-4 rounded-xl border backdrop-blur-md w-[160px]"
        style={{
          backgroundColor: theme === "white" ? "#ffffff" : colors.background,
          borderColor: colors.border,
          boxShadow:
            theme === "white"
              ? "0 4px 14px rgba(15, 23, 42, 0.08)"
              : "0 8px 24px rgba(0, 0, 0, 0.4)",
        }}
      >
        {/* PANEL HEADER WITH THEME-SAFE HOVER INFO TOOLTIP */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-[13px] font-bold" style={{ color: colors.text }}>
            Filters
          </div>

          <AntTooltip
            placement="leftTop"
            title={
              <div className="space-y-2 text-xs p-1">
                <div className="font-bold text-[13px] border-b border-white/20 pb-2 mb-2">
                  Communication status - time since last report
                </div>
                <div className="flex items-start gap-2">
                  <div
                    className="w-2 h-2 rounded-full mt-1 shrink-0"
                    style={{ backgroundColor: "#00e5a8" }}
                  />
                 <div className="flex flex-col">
                    <strong>Reporting</strong>
                    <span className="mt-0.5 opacity-90">Reported within the last 30 days</span>
                  </div>
                </div>
               <div className="flex items-start gap-2">
                  <div
                    className="w-2 h-2 rounded-full mt-1 shrink-0"
                    style={{ backgroundColor: "#ffa940" }}
                  />
                  <div className="flex flex-col">
                    <strong>Overdue</strong>
                    <span className="mt-0.5 opacity-90">Last reported 30-90 days</span>
                  </div>
                </div>
                
                <div className="flex items-start gap-2">
                  <div
                    className="w-2 h-2 rounded-full mt-1 shrink-0"
                    style={{ backgroundColor: "#ff4d4f" }}
                  />
                  <div className="flex flex-col">
                    <strong>Offline</strong>
                    <span className="mt-0.5 opacity-90">Last reported 90 + days</span>
                  </div>
                </div>
              </div>
            }
            overlayInnerStyle={{
              backgroundColor:
                theme === "white" ? "#1e293b" : colors.background,
              color: "#ffffff",
              border: `1px solid ${colors.border}`,
              borderRadius: "8px",
              padding: "10px",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke={colors.textSecondary}
              strokeWidth="4.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="cursor-pointer hover:opacity-80"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </AntTooltip>
        </div>

        <div className="flex gap-1 mb-2">
          <button
            onClick={() => setFilterType("active")}
            className="flex-1 whitespace-nowrap text-[11px] p-1 rounded font-semibold"
            style={{
              background:
                filterType === "active"
                  ? colors.gradient
                  : theme === "white"
                    ? "#f1f5f9"
                    : colors.cardBg,
              color: filterType === "active" ? "#ffffff" : colors.text,
              border: `1px solid ${filterType === "active" ? "transparent" : colors.border}`,
              cursor: "pointer",
            }}
          >
            In Service
          </button>
          <button
            onClick={() => setFilterType("all")}
            className="flex-1 text-[11px] p-1 rounded font-semibold"
            style={{
              background:
                filterType === "all"
                  ? colors.gradient
                  : theme === "white"
                    ? "#f1f5f9"
                    : colors.cardBg,
              color: filterType === "all" ? "#ffffff" : colors.text,
              border: `1px solid ${filterType === "all" ? "transparent" : colors.border}`,
              cursor: "pointer",
            }}
          >
            All
          </button>
        </div>

        {/* Added Communication status label from the mockup */}
        <div className="text-[10px] font-bold mb-2 mt-3" style={{ color: colors.textSecondary }}>
          Communication status
        </div>

        {/* CLEAN LIST MAP WITHOUT INDIVIDUAL ROW TOOLTIPS */}
        {["all", "green", "orange", "red"].map((f) => {
          const filterLabels = {
            all: "All meters",
            green: "Reporting",
            orange: "Overdue",
            red: "Offline",
          };

          return (
            <div
              key={f}
              onClick={() => setColorFilter(f)}
              className="flex items-center justify-between mb-1 cursor-pointer transition-colors"
              style={{
                backgroundColor:
                  colorFilter === f
                    ? theme === "white"
                      ? "#f1f5f9"
                      : `${colors.primary}20`
                    : "transparent",
                border:
                  colorFilter === f
                    ? `1px solid ${colors.primary}40`
                    : "1px solid transparent",
                borderRadius: "6px",
                padding: "4px 6px",
              }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{
                    background:
                      f === "all"
                        ? theme === "white"
                          ? "#64748b"
                          : "#ccc"
                        : f === "green"
                          ? "#00e5a8"
                          : f === "orange"
                            ? "#ffa940"
                            : "#ff4d4f",
                  }}
                />
                <span
                  className="text-[11px] font-medium"
                  style={{ color: colors.text }}
                >
                  {filterLabels[f]}
                </span>
              </div>
              <span
                className="text-[10px] font-bold"
                style={{ color: colors.textSecondary }}
              >
                {operationalCounts[f]}
              </span>
            </div>
          );
        })}
      </div>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapPosition.center} // Use the state center
        zoom={mapPosition.zoom} // Use the state zoom
        onLoad={onMapLoad}
        onDragEnd={syncMapPosition} // Sync state when user stops dragging
        onZoomChanged={syncMapPosition} // Sync state when user zooms
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          gestureHandling: "greedy",
        }}
      >
        {/* <MarkerClustererF onLoad={(c) => (clustererRef.current = c)}>
          {() => null}
        </MarkerClustererF> */}
        {/* <MarkerClustererF
          onLoad={(c) => (clustererRef.current = c)}
          options={{
            maxZoom: 19, // Forces clusters to turn into markers at zoom 19
            gridSize: 40, // Smaller grid prevents markers from being "eaten" too easily
          }}
        >
          {() => null}
        </MarkerClustererF> */}
        <MarkerClustererF
          onLoad={(c) => (clustererRef.current = c)}
          options={{
            maxZoom: 19, // Forces clusters to turn into markers at zoom 19
            gridSize: 40, // Smaller grid prevents markers from being "eaten" too easily
            // Forces all tiers (yellow, red, etc.) to use your blue asset (m1.png)
            styles: Array(5).fill({
              url: clusterIcon,
              height: 53,
              width: 53,
              textColor: "#ffffff",
              textSize: 10,
            }),
          }}
        >
          {() => null}
        </MarkerClustererF>
      </GoogleMap>

      <Modal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          isReturningRef.current = true;

          // Re-trigger the state position to force map back
          setMapPosition((prev) => ({ ...prev }));

          setTimeout(() => {
            isReturningRef.current = false;
          }, 600);
        }}
        footer={null}
        width="82%"
        centered
        styles={{
          content: {
            backgroundColor: colors.background,
            padding: 0,
            border: `1px solid ${colors.border}`,
          },
        }}
        closeIcon={false}
      >
        {selectedMeterData && (
          <div className="flex flex-col h-[85vh]">
            <div
              className="flex items-center justify-between p-6 border-b"
              style={{ borderColor: colors.border }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-lg border flex items-center justify-center"
                  style={{
                    backgroundImage: colors.gradient,
                    borderColor: colors.primary,
                  }}
                >
                  <Gauge className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2
                    className="text-2xl font-bold"
                    style={{ color: colors.text }}
                  >
                    Meter Usage
                  </h2>
                  <p
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    ID: {selectedMeterData.meter_id}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-10 h-10 rounded-lg border flex items-center justify-center hover:bg-white/10"
              >
                <X
                  className="w-5 h-5"
                  style={{ color: colors.textSecondary }}
                />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-12 gap-6">
                <div
                  className="col-span-12 lg:col-span-8 border rounded-lg p-4"
                  style={{
                    borderColor: colors.border,
                    backgroundColor: colors.cardBg,
                  }}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 style={{ color: colors.text, margin: 0 }}>
                      <TrendingUp size={18} className="inline mr-2" />
                      Consumption Flow
                    </h3>
                    <div>
                      <div className="flex items-center gap-3 justify-end mb-5">
                        <span
                          className="text-xs font-bold"
                          style={{ color: colors.textSecondary }}
                        >
                          Range:
                        </span>
                        <ConfigProvider
                          theme={{
                            algorithm:
                              theme === "dark"
                                ? antTheme.darkAlgorithm
                                : antTheme.defaultAlgorithm,
                            token: {
                              // General text and background for the dropdown panel
                              colorText: colors.text,
                              colorBgElevated: colors.cardBg, // Ensures the popup background is dark
                              colorPrimary: colors.primary, // Your dashboard primary color
                              colorTextHeading: colors.text,
                            },
                            components: {
                              DatePicker: {
                                // Fixes white-on-white text inside selected date bubbles
                                colorTextLightSolid: "#ffffff",
                                // Ensures navigation arrows and close (clear) icons are visible
                                colorIcon: colors.textSecondary,
                                colorIconHover: colors.primary,
                                // Optional: color for the non-selected dates in the range
                                controlItemBgActive: `${colors.primary}33`,
                              },
                            },
                          }}
                        >
                          <RangePicker
                            value={[dayjs(startDate), dayjs(endDate)]}
                            onChange={(d) => {
                              if (d) {
                                setStartDate(d[0].format("YYYY-MM-DD"));
                                setEndDate(d[1].format("YYYY-MM-DD"));
                              }
                            }}
                            style={{
                              backgroundColor: colors.background,
                              borderColor: colors.border,
                              color: colors.text,
                            }}
                            // Prevents potential z-index or global CSS conflicts
                            classNames={{
                              popup: {
                                root: "dark-theme-picker",
                              },
                            }}
                          />
                        </ConfigProvider>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col justify-center relative">
                    {isModalLoading ? (
                      <div className="flex flex-col items-center gap-3">
                        <Spin size="large" />
                        <span style={{ color: colors.textSecondary }}>
                          Fetching Data...
                        </span>
                      </div>
                    ) : anomalyData.length === 0 ? (
                      /* THE EMPTY MESSAGE */
                      <div className="flex flex-col items-center justify-center text-center p-10">
                        <Activity
                          size={48}
                          style={{ color: colors.textSecondary, opacity: 0.2 }}
                        />
                        <p
                          className="mt-4 text-lg font-medium"
                          style={{ color: colors.textSecondary }}
                        >
                          No consumption data found
                        </p>
                        <p
                          className="text-sm"
                          style={{ color: colors.textSecondary, opacity: 0.7 }}
                        >
                          Try adjusting the selected date range.
                        </p>
                      </div>
                    ) : (
                      <>
                        <RenderCustomLegend />
                        <div className="h-[575px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={anomalyData}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke={colors.border}
                              />
                              <XAxis
                                dataKey="name"
                                stroke={colors.textSecondary}
                                fontSize={10}
                                tickFormatter={(v) => dayjs(v).format("MMM DD")}
                              />
                              <YAxis
                                stroke={colors.textSecondary}
                                fontSize={10}
                              />
                              <Tooltip content={<AnomalyTooltip />} />
                              <Line
                                type="monotone"
                                dataKey="threshold"
                                stroke={colors.textSecondary}
                                strokeDasharray="5 5"
                                dot={false}
                                opacity={0.4}
                              />
                              <Line
                                type="monotone"
                                dataKey="rolling"
                                stroke={colors.textSecondary}
                                strokeDasharray="1 4"
                                dot={false}
                                opacity={0.4}
                              />
                              <Line
                                type="monotone"
                                dataKey="gallons"
                                stroke={colors.primary}
                                strokeWidth={3}
                                dot={(p) =>
                                  p.payload.isAnomaly ? (
                                    <Dot
                                      {...p}
                                      r={5}
                                      fill="#ef4444"
                                      stroke="#fff"
                                    />
                                  ) : (
                                    <Dot {...p} r={2} fill={colors.primary} />
                                  )
                                }
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div
                  className="col-span-12 lg:col-span-4 space-y-4 border rounded-lg p-4 h-[575px] overflow-y-auto"
                  style={{
                    borderColor: colors.border,
                    backgroundColor: colors.cardBg,
                  }}
                >
                  <h4
                    style={{ fontSize: "20px", color: colors.primary }}
                    className="mb-3"
                  >
                    Meter Information
                  </h4>
                  <InfoTile
                    label="Meter ID"
                    value={selectedMeterData.meter_id}
                    icon={<Hash size={18} />}
                  />
                  <SectionHeader
                    title="Meter Details"
                    icon={<Gauge size={18} />}
                  />
                  <InfoTile
                    label="Serial Number"
                    value={hardwareDetails?.meter_sn}
                    icon={<Gauge size={18} />}
                  />
                  <div
                    className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3"
                    style={{
                      backgroundColor: `${colors.cardBg}0D`,
                      borderColor: `${colors.primary}40`,
                    }}
                  >
                    <Calendar className="text-cyan-400 mt-1" size={16} />
                    <div>
                      <span
                        className="text-xs font-semibold"
                        style={{ color: colors.textSecondary }}
                      >
                        Install Date
                      </span>
                      <p
                        className="text-sm mt-1"
                        style={{ color: colors.text }}
                      >
                        {hardwareDetails?.meter_install_date}
                      </p>
                    </div>
                  </div>
                  <SectionHeader
                    title="Status Alerts"
                    icon={<AlertTriangle size={18} />}
                  />
                  <div
                    className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3"
                    style={{
                      backgroundColor: `${statusColor}10`,
                      borderColor: `${statusColor}50`,
                    }}
                  >
                    <Activity
                      className="mt-1"
                      size={18}
                      style={{ color: statusColor }}
                    />
                    <p
                      className="text-sm font-medium mt-1"
                      style={{ color: statusColor }}
                    >
                      {hardwareDetails?.m_endpoint_status}
                    </p>
                  </div>
                  <SectionHeader title="Location" icon={<MapPin size={18} />} />
                  <div
                    className="p-4 rounded-xl border"
                    style={{
                      backgroundColor: colors.cardBg,
                      borderColor: colors.border,
                    }}
                  >
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: colors.text }}
                    >
                      {hardwareDetails?.service_address ||
                        selectedMeterData.location_address}
                    </p>
                  </div>
                  <SectionHeader
                    title="System Status"
                    icon={<Activity size={18} />}
                  />
                  <div className="grid grid-cols-2 gap-3 max-[767px]:grid-cols-1">
                    <div
                      className="backdrop-blur-sm rounded-xl border p-4"
                      style={{
                        backgroundColor: colors.cardBg,
                        borderColor: colors.border,
                      }}
                    >
                      <div className="flex items-center gap-2 mb-1 text-green-400">
                        <Battery size={16} />
                        <span className="text-[10px] font-bold text-slate-400">
                          Battery
                        </span>
                      </div>
                      <p
                        className="text-sm font-bold"
                        style={{ color: colors.text }}
                      >
                        {hardwareDetails?.battery_level}
                      </p>
                    </div>
                    <div
                      className="backdrop-blur-sm rounded-xl border p-4"
                      style={{
                        backgroundColor: colors.cardBg,
                        borderColor: colors.border,
                      }}
                    >
                      <div className="flex items-center gap-2 mb-1 text-cyan-400">
                        <Wifi size={16} />
                        <span className="text-[10px] font-bold text-slate-400">
                          Signal
                        </span>
                      </div>
                      <p
                        className="text-sm font-bold"
                        style={{ color: colors.text }}
                      >
                        {hardwareDetails?.signal_strength}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MapTab;
